import { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../services/authService';

export const useAdminVendorSpotlight = (initialFilters = {}) => {
  const queryClient = useQueryClient();
  const [filters, setFilters] = useState({
    is_active: '',
    search: '',
    ...initialFilters
  });

  const activeStatusOptions = [
    { value: '', label: 'All Spotlights' },
    { value: 'true', label: 'Active' },
    { value: 'false', label: 'Inactive' }
  ];

  const cleanFilters = useMemo(() => {
    return Object.fromEntries(
      Object.entries(filters).filter(([_, value]) => value !== '' && value !== null && value !== undefined)
    );
  }, [filters]);

  const cleanFiltersWithPagination = useMemo(() => {
    return {
      ...cleanFilters,
      per_page: cleanFilters.per_page || 10, // Default to 10 items per page
    };
  }, [cleanFilters]);

  const queryString = new URLSearchParams(cleanFiltersWithPagination).toString();

  const { data: spotlightsResponse, isLoading, error } = useQuery({
    queryKey: ['adminVendorSpotlights', cleanFiltersWithPagination],
    queryFn: async () => {
      const response = await api.get(`/admin/vendor-spotlights${queryString ? `?${queryString}` : ''}`);
      return response.data;
    }
  });

  const { data: statisticsResponse } = useQuery({
    queryKey: ['adminVendorSpotlightStatistics'],
    queryFn: async () => {
      const response = await api.get('/admin/vendor-spotlights/statistics');
      return response.data;
    }
  });

  const createMutation = useMutation({
    mutationFn: async (data) => {
      const response = await api.post('/admin/vendor-spotlights', data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['adminVendorSpotlights']);
      queryClient.invalidateQueries(['adminVendorSpotlightStatistics']);
    }
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, data }) => {
      const response = await api.put(`/admin/vendor-spotlights/${id}`, data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['adminVendorSpotlights']);
      queryClient.invalidateQueries(['adminVendorSpotlightStatistics']);
    }
  });

  const toggleStatusMutation = useMutation({
    mutationFn: async (id) => {
      const response = await api.patch(`/admin/vendor-spotlights/${id}/toggle-status`);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['adminVendorSpotlights']);
      queryClient.invalidateQueries(['adminVendorSpotlightStatistics']);
    }
  });

  const deleteMutation = useMutation({
    mutationFn: async (id) => {
      const response = await api.delete(`/admin/vendor-spotlights/${id}`);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['adminVendorSpotlights']);
      queryClient.invalidateQueries(['adminVendorSpotlightStatistics']);
    }
  });

  const spotlights = useMemo(() => {
    // Handle paginated response from Laravel
    if (spotlightsResponse?.data?.data) {
      return spotlightsResponse.data.data; // Paginated data
    }
    return spotlightsResponse?.data || [];
  }, [spotlightsResponse]);


  const statistics = useMemo(() => {
    return statisticsResponse?.data || {
      total_spotlights: 0,
      active_spotlights: 0,
      inactive_spotlights: 0
    };
  }, [statisticsResponse]);

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const handleSearch = (data, searchTerm) => {
    if (!searchTerm) return data;
    
    return data.filter(spotlight => 
      spotlight.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      spotlight.category?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      spotlight.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      spotlight.location?.toLowerCase().includes(searchTerm.toLowerCase())
    );
  };

  return {
    spotlights,
    isLoading,
    error,
    filters,
    activeStatusOptions,
    handleFilterChange,
    handleSearch,
    statistics,
    createSpotlight: (data, options) => createMutation.mutate(data, options),
    isCreating: createMutation.isPending,
    updateSpotlight: (data, options) => updateMutation.mutate(data, options),
    isUpdating: updateMutation.isPending,
    toggleStatus: (id, options) => toggleStatusMutation.mutate(id, options),
    isToggling: toggleStatusMutation.isPending,
    deleteSpotlight: (id, options) => deleteMutation.mutate(id, options),
    isDeleting: deleteMutation.isPending
  };
};

